import React, { FC, useState } from 'react';
import './BatchActions.scss';
import Button from '../../../components/buttons/Button';
import { OrderListOrderClazz, OrderStatus } from '../../../types/orderTypes';
import { fetchOrderListOrdersById } from '../../../fetch/orderFetch';
import dayjs from 'dayjs';
import SelectDeliveryDateMultipleModal from '../../../components/modals/SelectDeliveryDateMultipleModal';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { cancelListOfOrders, markMultipleOrdersAsReady } from '../OrdersUtil';

interface ParsedOrder {
  orderId: string;
  order?: OrderListOrderClazz;
  exists: boolean;
  selected: boolean;
  deliveryTime?: string;
}

export const BatchActions: FC = () => {
  const [rawOrderIdList, setRawOrderIdList] = useState('');
  const [parsedOrderList, setParsedOrderList] = useState<ParsedOrder[]>([]);
  const [fetchingOrders, setFetchingOrders] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  const checkOrders = async () => {
    const rawOrderIds = rawOrderIdList
      .split(/(\r\n|\r|\n)/)
      .map(rawOrderId => rawOrderId.trim())
      .filter(rawOrderId => rawOrderId !== '' && /^(\d+)$/.test(rawOrderId));

    const distinctOrderIds = [...new Set(rawOrderIds)];
    await fetchOrders(distinctOrderIds);
  };

  const fetchOrders = async (orderIds: string[]) => {
    try {
      setFetchingOrders(true);
      const fetchedOrders = await fetchOrderListOrdersById(orderIds);
      const newParsedOrders: ParsedOrder[] = orderIds.map(rawOrderId => {
        const fetchedOrder = fetchedOrders.find(
          order => order.id === rawOrderId
        );
        const deliveryTime = fetchedOrder
          ? `${dayjs(fetchedOrder.deliverFrom).format('DD.MM HH:mm')}-${dayjs(
              fetchedOrder.deliverUntil
            ).format('HH:mm')}`
          : undefined;
        return {
          orderId: rawOrderId,
          exists: Boolean(fetchedOrder),
          selected: false,
          order: fetchedOrder
            ? new OrderListOrderClazz(fetchedOrder)
            : undefined,
          deliveryTime: deliveryTime,
        };
      });
      const notRefreshedOrders = parsedOrderList.filter(
        parsedOrder => !orderIds.includes(parsedOrder.orderId)
      );
      setParsedOrderList([...notRefreshedOrders, ...newParsedOrders]);
      setRawOrderIdList('');
    } finally {
      setFetchingOrders(false);
    }
  };

  const toggleSelectOrder = (orderId: string) => {
    setParsedOrderList(
      parsedOrderList.map(parsedOrder => {
        if (parsedOrder.orderId === orderId) {
          return {
            ...parsedOrder,
            selected: !parsedOrder.selected,
          };
        } else {
          return parsedOrder;
        }
      })
    );
  };

  const removeSelected = () => {
    setParsedOrderList(
      parsedOrderList.filter(parsedOrder => !parsedOrder.selected)
    );
  };

  const removeInvalid = () => {
    setParsedOrderList(
      parsedOrderList.filter(parsedOrder => parsedOrder.exists)
    );
  };

  const validOrdersPresent = parsedOrderList.some(
    parsedOrder => parsedOrder.exists
  );

  const cancelSelectedOrders = async () => {
    const orders = parsedOrderList.filter(
      order => order.exists && order.order?.status != OrderStatus.Canceled
    );
    const result: SweetAlertResult<string> = await Swal.fire({
      title: 'Confirm cancelling orders',
      text: `Are you sure you want to cancel ${orders.length} orders? This is an irreversible action.`,
      input: 'textarea',
      icon: 'warning',
      inputPlaceholder: 'Type your reason for cancelling here...',
      showCancelButton: true,
      confirmButtonText: 'Cancel orders',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Abort',
      cancelButtonColor: '#ccc',
    });
    if (result.isConfirmed && result.value) {
      const orderIds = orders.map(order => order.orderId);
      const swalPromise = Swal.fire({
        title: `Cancelling ${orders.length} orders`,
      });
      Swal.showLoading();
      const cancelResponse = await cancelListOfOrders(orderIds, result.value);
      if (cancelResponse.success) {
        Swal.update({
          text: 'Orders cancelled successfully!',
          icon: 'success',
        });
      } else {
        Swal.update({
          text: `Cancelling orders failed, error message: ${cancelResponse.error}`,
          icon: 'error',
        });
      }
      Swal.hideLoading();
      await swalPromise;
      await fetchOrders(orderIds);
    }
  };

  const markOrdersAsReady = async () => {
    const orders = parsedOrderList.filter(parsedOrder => parsedOrder.exists);
    const result: SweetAlertResult<boolean> = await Swal.fire({
      title: 'Confirm marking orders orders',
      text: `Are you sure you want to mark ${orders.length} orders as ready?`,
      input: 'checkbox',
      inputPlaceholder: 'Include currently cancelled orders',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Mark orders as ready',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Abort',
      cancelButtonColor: '#ccc',
    });
    if (result.isConfirmed) {
      const orderIds = orders.map(order => order.orderId);
      const swalPromise = Swal.fire({
        title: `Marking ${orders.length} orders as ready`,
      });
      Swal.showLoading();
      const markAsReadyResponse = await markMultipleOrdersAsReady(
        orderIds,
        result.value || false
      );
      if (markAsReadyResponse.ok) {
        Swal.update({
          text: 'Orders successfully marked as ready!',
          icon: 'success',
        });
      } else {
        Swal.update({
          text: `Cancelling orders failed with status ${markAsReadyResponse.status}`,
          icon: 'error',
        });
      }
      Swal.hideLoading();
      await swalPromise;
      await fetchOrders(orderIds);
    }
  };

  const onCloseRescheduleModal = () => {
    setShowRescheduleModal(false);
    fetchOrders(parsedOrderList.map(order => order.orderId)).then();
  };

  return (
    <div className="batch-actions-background">
      <div className="batch-actions-content">
        <div className="batch-actions-row">
          <div className="batch-actions-column" style={{ width: '25%' }}>
            <span>Add order ids here (one per line):</span>
            <textarea
              title="Insert order ids here, one per line"
              className="batch-actions-id-input"
              value={rawOrderIdList}
              onChange={event => setRawOrderIdList(event.target.value)}
              placeholder="1234567"
            />
          </div>
          <div
            className="batch-actions-column"
            style={{ justifyContent: 'center' }}
          >
            <Button
              title={'\u21E8'}
              style={{ minWidth: '80px' }}
              disabled={rawOrderIdList.trim() === '' || fetchingOrders}
              onClick={() => checkOrders()}
            />
          </div>
          <div className="batch-actions-column" style={{ width: '40%' }}>
            <span>Recipient orders:</span>
            <table className="batch-actions-orders-table">
              <tbody>
                {parsedOrderList.map(parsedOrder => (
                  <tr
                    className={
                      parsedOrder.selected
                        ? 'selected-order-row'
                        : 'unselected-order-row'
                    }
                    key={`parsedOrder_${parsedOrder.orderId}`}
                    onClick={() => toggleSelectOrder(parsedOrder.orderId)}
                  >
                    <td>{parsedOrder.exists ? <>&#9989;</> : <>&#10060;</>}</td>
                    <td>{parsedOrder.orderId}</td>
                    <td>{parsedOrder.order?.status}</td>
                    <td>{parsedOrder.order?.region}</td>
                    <td style={{ width: '100%', textAlign: 'right' }}>
                      {parsedOrder.deliveryTime}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: '10px' }}>
              <Button
                title="Remove all"
                onClick={() => setParsedOrderList([])}
                disabled={parsedOrderList.length === 0}
              />
              <Button
                title="Remove selected"
                onClick={() => removeSelected()}
                disabled={
                  !parsedOrderList.some(parsedOrder => parsedOrder.selected)
                }
              />
              <Button
                title="Remove invalid"
                onClick={() => removeInvalid()}
                disabled={
                  !parsedOrderList.some(parsedOrder => !parsedOrder.exists)
                }
              />
            </div>
          </div>
          <div className="batch-actions-column">
            <span>
              Perform on{' '}
              {parsedOrderList.filter(parsedOrder => parsedOrder.exists).length}{' '}
              orders:
            </span>
            <Button
              className="batch-actions-actionbutton"
              title="Cancel orders"
              disabled={!validOrdersPresent}
              onClick={() => cancelSelectedOrders()}
            />
            <Button
              className="batch-actions-actionbutton"
              disabled={!validOrdersPresent}
              title="Reschedule"
              onClick={() => setShowRescheduleModal(true)}
            />
            <Button
              className="batch-actions-actionbutton"
              title="Mark as ready"
              disabled={!validOrdersPresent}
              onClick={() => markOrdersAsReady()}
            />
          </div>
        </div>
      </div>
      {showRescheduleModal && (
        <SelectDeliveryDateMultipleModal
          orders={parsedOrderList
            .filter(order => order.exists)
            .map(order => order.order)}
          triggerReload={false}
          onCloseModal={() => onCloseRescheduleModal()}
        />
      )}
    </div>
  );
};
